import React, { ChangeEvent, KeyboardEvent, useState } from "react";
import { GrProgressCircle, GrStack } from "@jnpmedi/grebob";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Pagination,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { usePaginateGovAddressQuery } from "@newHooks/query/govAddress/paginateGovAddress.query";
import { GovAddressSortType } from "@maven-rest/docs/GovAddress.dto";

interface GovAddressDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (address: string) => void;
}

const itemsPerPage = 4; // 페이지 당 아이템 수
const MAX_DETAIL_ADDRESS_LEN = 25; // 상세주소 최대 입력길이

export function GovAddressDialog({ open, onClose, onConfirm }: GovAddressDialogProps) {
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<GovAddressSortType>(GovAddressSortType.Accuracy);
  const [includeChangedAddress, setIncludeChangedAddress] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [detailAddress, setDetailAddress] = useState<string>("");
  const [totalCount, setTotalCount] = useState<number>(0);

  const {
    data: govAddressData,
    isInitialLoading: isGovAddressInitialLoading,
    isError: isGovAddressError,
    error: govAddressError,
  } = usePaginateGovAddressQuery(
    {
      keyword: searchKeyword,
      includeChangedAddress: includeChangedAddress,
      sort: selectedOption,
      page: page,
      rpp: itemsPerPage,
    },
    {
      enabled: !selectedAddress && searchKeyword.length > 0,
      onSuccess: (data) => {
        const count = Number(data.count);
        setTotalCount(count);
      },
      onError: () => {
        setTotalCount(0);
      },
    }
  );

  const handleSearch = () => {
    setSearchKeyword(inputValue);
    setSelectedAddress(null);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value as GovAddressSortType);
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIncludeChangedAddress(event.target.checked);
  };

  const handlePageChange = (_: ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleRowClick = (address: string) => {
    setSelectedAddress(address);
  };

  const handleDetailAddressChange = (address: string) => {
    const slicedAddress = address.length > MAX_DETAIL_ADDRESS_LEN ? address.substring(0, MAX_DETAIL_ADDRESS_LEN) : address;

    setDetailAddress(slicedAddress);
  };

  const handleConfirm = () => {
    if (selectedAddress) {
      onConfirm(`${selectedAddress} ${detailAddress}`);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", background: "#F4F6F9", p: 0 }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            padding: "16px 16px 10px 16px",
          }}
        >
          <CloseIcon
            sx={{
              color: "#4D5358",
              width: "18px",
              height: "18px",
            }}
          />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, borderTop: 0 }}>
        <Box sx={{ background: "#F4F6F9", padding: "8px 24px 6px 24px" }}>
          <TextField
            fullWidth
            placeholder="도로명, 건물명, 번지 검색"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)} // 입력 값을 업데이트
            onKeyPress={handleKeyPress}
            sx={{ mb: 1, background: "#FFFFFF" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {totalCount >= 1000 && (
            <Box display="flex" sx={{ marginBottom: "12px", marginTop: "10px" }}>
              <ErrorIcon
                sx={{
                  color: "#697077",
                  marginRight: "4px",
                }}
              />
              <Typography sx={{ fontSize: "13px", color: "#4D5358", fontWeight: 500 }}>
                검색결과가 너무 많습니다.
                <span
                  style={{
                    fontSize: "13px",
                    color: "#697077",
                    fontWeight: 400,
                  }}
                >
                  {" 찾으시려는 도로명주소의 건물번호 또는 건물명까지 상세히 입력 후 검색해 주세요."}
                </span>
              </Typography>
            </Box>
          )}

          <Box display="flex" alignItems="center">
            <Checkbox checked={includeChangedAddress} onChange={handleCheckboxChange} size="small" sx={{ p: 0, marginRight: "6px" }} />
            <Typography sx={{ fontSize: "12px" }}>변경된 주소정보 포함</Typography>
          </Box>
        </Box>
        {selectedAddress ? (
          <Box sx={{ background: "#FFFFFF", p: 3 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "bold",
                mb: 2,
              }}
            >
              상세주소 입력
            </Typography>
            <Box sx={{ display: "flex", mb: 2 }}>
              <Typography sx={{ width: "100px" }}>도로명 주소</Typography>
              <Typography>{selectedAddress}</Typography>
            </Box>
            <Box sx={{ display: "flex", mb: 2 }}>
              <Typography sx={{ width: "100px" }}>상세주소</Typography>
              <TextField
                fullWidth
                placeholder="상세 주소 입력"
                value={detailAddress}
                onChange={(e) => handleDetailAddressChange(e.target.value)}
                sx={{ background: "#F4F6F9" }}
              />
            </Box>
            <Box display="flex" justifyContent="center">
              <Button variant="contained" color="primary" onClick={handleConfirm} disabled={!detailAddress}>
                주소 등록
              </Button>
            </Box>
          </Box>
        ) : searchKeyword ? (
          <>
            {isGovAddressInitialLoading ? (
              <GrStack height="320px" justifyContent="center" alignItems="center">
                <GrProgressCircle size={20} variant="indeterminate" />
              </GrStack>
            ) : (
              <>
                {isGovAddressError ? (
                  <>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      sx={{ padding: "16px 24px 14px 24px" }}
                    >
                      <Typography sx={{ textOverflow: "ellipsis", fontSize: 16 }}>0건</Typography>
                    </Box>
                    <Box display={"flex"} flexDirection={"column"} alignItems={"center"} sx={{ padding: "16px 24px" }}>
                      <ErrorIcon
                        sx={{
                          width: "33px",
                          height: "33px",
                          color: "#FB7211",
                          mb: "16px",
                        }}
                      />
                      <Typography sx={{ fontSize: 16, fontWeight: 500, mb: "8px" }}>검색결과에 오류가 있습니다.</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 400 }}>{govAddressError.responseMessage}</Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    {totalCount === 0 ? (
                      <>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          flexWrap="wrap"
                          sx={{ padding: "16px 24px 14px 24px" }}
                        >
                          <Typography sx={{ textOverflow: "ellipsis", fontSize: 16 }}>0건</Typography>
                        </Box>
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ padding: "16px 24px" }}>
                          <ErrorIcon
                            sx={{
                              width: "33px",
                              height: "33px",
                              color: "#C1C7CD",
                              mb: "16px",
                            }}
                          />
                          <Typography sx={{ fontSize: 16, fontWeight: 500, mb: "8px" }}>검색 결과가 없습니다.</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 400 }}>검색어에 잘못된 철자가 없는지,</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 400 }}>정확한 주소인지 다시 확인해 주세요.</Typography>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          flexWrap="wrap"
                          sx={{ padding: "16px 24px 14px 24px" }}
                        >
                          <Typography sx={{ textOverflow: "ellipsis", fontSize: 16 }}>{String(govAddressData?.count || 0)}건</Typography>
                          <RadioGroup row value={selectedOption} onChange={handleOptionChange}>
                            <FormControlLabel
                              value={GovAddressSortType.Accuracy}
                              control={<Radio size="small" />}
                              label={<Typography sx={{ fontSize: 13 }}>정확도 순</Typography>}
                            />
                            <FormControlLabel
                              value={GovAddressSortType.Road}
                              control={<Radio size="small" />}
                              label={<Typography sx={{ fontSize: 13 }}>도로명 포함</Typography>}
                            />
                            <FormControlLabel
                              value={GovAddressSortType.Location}
                              control={<Radio size="small" />}
                              label={<Typography sx={{ fontSize: 13 }}>지번 포함</Typography>}
                            />
                          </RadioGroup>
                        </Box>
                        <List>
                          {govAddressData?.items.map((item, index) => (
                            <ListItem
                              key={index}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                borderBottom: "1px solid #F0F2F4",
                                padding: "16px 24px",
                                cursor: "pointer",
                              }}
                              onClick={() => handleRowClick(item.roadAddr)}
                            >
                              <Box>
                                <Box display="flex" mb={1}>
                                  <Box sx={{ background: "#F0F2F4", borderRadius: 1, mr: 2, width: "46px", textAlign: "center" }}>
                                    <Typography variant="body2" color="textSecondary">
                                      도로명
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2">{item.roadAddr}</Typography>
                                </Box>
                                <Box display="flex">
                                  <Box sx={{ background: "#F0F2F4", borderRadius: 1, mr: 2, width: "46px", textAlign: "center" }}>
                                    <Typography variant="body2" color="textSecondary">
                                      지번
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2">{item.jibunAddr}</Typography>
                                </Box>
                              </Box>
                              <Typography sx={{ width: 56, textAlign: "center", color: "#0B86B2", fontSize: 18, fontWeight: 500 }}>
                                {item.zipNo}
                              </Typography>
                            </ListItem>
                          ))}
                        </List>
                        <Box display="flex" justifyContent="center" mt={2} pb={2}>
                          <Pagination
                            count={Math.ceil(Number(govAddressData?.count || 0) / itemsPerPage)}
                            page={page}
                            onChange={handlePageChange}
                          />
                        </Box>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <Box sx={{ background: "white", p: 3 }}>
            <Typography variant="h6" gutterBottom>
              검색 tip
            </Typography>
            <Typography variant="body2">• 도로명 + 건물 번호</Typography>
            <Typography variant="body2" sx={{ color: "blue", mb: 1 }}>
              컨벤시아대로 165
            </Typography>
            <Typography variant="body2">• 동/읍/면/리 + 번지</Typography>
            <Typography variant="body2" sx={{ color: "blue", mb: 1 }}>
              송도동 6-10
            </Typography>
            <Typography variant="body2">• 건물명, 아파트명</Typography>
            <Typography variant="body2" sx={{ color: "blue", mb: 1 }}>
              포스코타워
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
